import { isCaseSettled } from '../../SummaryCaseTable/utils/helpers.js';
import { getIFMVUtil, getAVUtil } from '../../../../utils/getAVUtil.js';

/**
 * Creates rows for EvidenceComparisonTable by combining case data with evidence comparison data.
 * Evidence types are kept as DB values: "Unequal" and "Excessive".
 *
 * @param {Array} filteredCases - Filtered negotiation cases
 * @param {Object} evidenceData - Evidence data grouped by parcel_id and Type
 * @returns {Array} Array of row objects for DataGrid
 */
export const createEvidenceComparisonRows = (filteredCases, evidenceData) => {
  return filteredCases.map((caseItem, index) => {
    const caseSettled = isCaseSettled(caseItem);
    const parcelId = caseItem.parcel_id || '';
    const parcelEvidence = evidenceData[parcelId] || {};
    // logger.log("parcelEvidence", parcelEvidence);
    
    // Calculate barFiled and scarFiled for isActive calculation
    const barFiled = caseItem.BARFiled === 1;
    const scarFiled = caseItem.SCARFiled !== 0;
    
    // Calculate isActive field - requires isSettled = false, and either barFiled or scarFiled = true
    // This indicates cases that are actively being processed (not settled and have been filed)
    const isActive = !caseSettled && (barFiled || scarFiled);
    
    // Map Type values: "Unequal" = Sales, "Excessive" = Assessment
    const salesEvidence = parcelEvidence['Unequal'] || parcelEvidence['unequal'] || null;
    const assessmentEvidence = parcelEvidence['Excessive'] || parcelEvidence['excessive'] || null;
    // logger.log("salesEvidence", salesEvidence);
    // logger.log("assessmentEvidence", assessmentEvidence);
    
    // Get score percentages
    const salesScore = salesEvidence?.CaseStrength ?? null;
    const assessmentScore = assessmentEvidence?.CaseStrength ?? null;
    
    // Use DB values as-is; no selection if empty or missing
    const selectedEvidenceTypes = (caseItem.SelectedEvidenceTypes || []).filter(
      (t) => t === 'Unequal' || t === 'Excessive'
    );
    
    // Get IFMV from case item
    const ifmv = getIFMVUtil(caseItem);
    
    // Calculate implied reduction for Sales workup
    let salesImpliedReduction = null;
    if (salesEvidence?.InitialMarketValue && ifmv) {
      salesImpliedReduction = ((salesEvidence.InitialMarketValue - ifmv) / ifmv) * 100;
    }
    
    // Calculate implied reduction for Assessment workup
    let assessmentImpliedReduction = null;
    if (assessmentEvidence?.InitialMarketValue && ifmv) {
      assessmentImpliedReduction = ((assessmentEvidence.InitialMarketValue - ifmv) / ifmv) * 100;
    }
    
    const row = {
      ...caseItem,  // Spread caseItem first
      id: parcelId,
      parcel_id: parcelId,
      Address: caseItem.Address || '',
      IFMV: ifmv,
      Status: caseSettled ? 'Settled' : 'Open',
      _isSettled: caseSettled,
      isActive: isActive,
      // Properties needed for OfferCell component (unified AV/IFMV for village vs town)
      EQ: caseItem.EQ,
      RAR: caseItem.RAR,
      PropertyAssessment: getAVUtil(caseItem),
      notFiledAtScar: caseItem.SCARFiled === 0,
      statusDisplay: caseItem.statusDisplay || '',
      OfferValueStyle: caseItem.OfferValueStyle || {},
      // Row index (0-based, but display as 1-based)
      RowIndex: index + 1,
      // Sales evidence data (from "Unequal" type)
      SalesMarketValue: salesEvidence?.InitialMarketValue ?? null,
      SalesStrength: salesEvidence?.CaseStrength ?? null,
      SalesImpliedReduction: salesImpliedReduction,
      SalesSelectedIndexRegular: salesEvidence?.SelectedIndexRegular ?? null,
      SalesSelectedIndexOptimize: salesEvidence?.SelectedIndexOptimize ?? null,
      // Assessment evidence data (from "Excessive" type)
      AssessmentMarketValue: assessmentEvidence?.InitialMarketValue ?? null,
      AssessmentStrength: assessmentEvidence?.CaseStrength ?? null,
      AssessmentImpliedReduction: assessmentImpliedReduction,
      AssessmentSelectedIndexRegular: assessmentEvidence?.SelectedIndexRegular ?? null,
      AssessmentSelectedIndexOptimize: assessmentEvidence?.SelectedIndexOptimize ?? null,
      // Selection state (DB values: Unequal, Excessive)
      selectedWorkupTypes: selectedEvidenceTypes,
      // S3 files from backend (enriched in load_client_ids)
      S3Files: caseItem.S3Files || [],
      NyscefS3Files: caseItem.NyscefS3Files || [],
    };
    
    return row;
  });
};

